/**
 * SERVICIO DE GESTIÓN DE ROLES Y PERMISOS
 * 
 * Este servicio centraliza todas las operaciones CRUD para roles y permisos,
 * conectándose con los endpoints del backend en Python/FastAPI.
 * 
 * Endpoints disponibles en el backend:
 * - Roles: /roles/*
 * - Permisos: /permisos/*  
 * - Relaciones: /rol-permisos/*
 * 
 * @author Miguel Bolivar
 * @version 1.0
 * @since 2025-11
 */

// Configuración de la API base
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL_RENDER || 'http://localhost:8000';

/**
 * Función auxiliar para manejar las peticiones HTTP
 * @param {string} endpoint - Endpoint de la API
 * @param {Object} options - Opciones de la petición (method, body, etc.)
 * @returns {Promise<Object>} Respuesta de la API
 */
const apiRequest = async (endpoint, options = {}) => {
  try {
    // Obtener token de autenticación
    const token = localStorage.getItem('access_token');
    
    // Configuración por defecto de las peticiones
    const config = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Agregar token si existe y no es una petición pública
    if (token && !token.includes('mock-token')) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    console.log(`Petición API: ${config.method} ${endpoint}`);
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    
    // Manejar respuestas no exitosas
    if (!response.ok) {
      const errorData = await response.text();
      console.error(`Error en ${endpoint}:`, {
        status: response.status,
        statusText: response.statusText,
        errorData
      });
      throw new Error(`Error ${response.status}: ${errorData}`);
    }

    const data = await response.json();
    console.log(`Respuesta exitosa de ${endpoint}:`, data);
    
    return data;
  } catch (error) {
    console.error(`Error en petición a ${endpoint}:`, error);
    throw error;
  }
};

/**
 * Servicio principal para gestión de roles y permisos
 */
const rolePermissionService = {
  
  // ==================== MÉTODOS ADMIN OPTIMIZADOS ====================
  
  /**
   * Obtiene la matriz completa de roles y permisos optimizada para la UI
   * Usa el endpoint admin específico que existe en el backend
   * @returns {Promise<Array>} Matriz de roles con permisos organizados
   */
  async getAdminMatrix() {
    return await apiRequest('/api/admin/roles-permissions/matrix');
  },

  /**
   * Toggle individual de permisos (para switches en tiempo real)
   * @param {number} roleId - ID del rol
   * @param {string} permissionId - ID del permiso
   * @param {boolean} estado - Nuevo estado del permiso
   * @returns {Promise<Object>} Resultado de la operación
   */
  async togglePermissionAdmin(roleId, permissionId, estado) {
    return await apiRequest('/api/admin/roles-permissions/toggle', {
      method: 'PUT',
      body: JSON.stringify({
        rol_id: roleId,
        id_permiso: permissionId,
        estado: estado
      }),
    });
  },

  /**
   * Actualización masiva de permisos (para botón "Guardar Cambios")
   * @param {Array} updates - Lista de cambios a aplicar
   * @returns {Promise<Object>} Resultado de la operación masiva
   */
  async updatePermissionsBulkAdmin(updates) {
    return await apiRequest('/api/admin/roles-permissions/bulk-update', {
      method: 'POST',
      body: JSON.stringify({ updates }),
    });
  },

  /**
   * Obtiene estadísticas de la configuración de permisos
   * @returns {Promise<Object>} Resumen estadístico
   */
  async getAdminStats() {
    return await apiRequest('/api/admin/roles-permissions/summary');
  },

  // ==================== OPERACIONES DE ROLES ====================
  
  /**
   * Obtiene todos los roles activos
   * @returns {Promise<Array>} Lista de roles activos
   */
  async getRoles() {
    return await apiRequest('/roles');
  },

  /**
   * Obtiene solo los roles activos
   * @returns {Promise<Array>} Lista de roles activos
   */
  async getActiveRoles() {
    return await apiRequest('/roles/activos');
  },

  /**
   * Obtiene un rol específico por ID
   * @param {number} roleId - ID del rol
   * @returns {Promise<Object>} Datos del rol
   */
  async getRoleById(roleId) {
    return await apiRequest(`/roles/${roleId}`);
  },

  /**
   * Crea un nuevo rol
   * @param {string} name - Nombre del rol
   * @returns {Promise<Object>} Rol creado
   */
  async createRole(name) {
    return await apiRequest('/roles', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  /**
   * Actualiza un rol existente
   * @param {number} roleId - ID del rol
   * @param {string} name - Nuevo nombre
   * @param {boolean} estado - Estado del rol
   * @returns {Promise<Object>} Rol actualizado
   */
  async updateRole(roleId, name, estado) {
    return await apiRequest(`/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify({ name, estado }),
    });
  },

  /**
   * Activa un rol
   * @param {number} roleId - ID del rol
   * @returns {Promise<Object>} Resultado de la operación
   */
  async activateRole(roleId) {
    return await apiRequest(`/roles/activar/${roleId}`, {
      method: 'PUT',
    });
  },

  /**
   * Desactiva un rol
   * @param {number} roleId - ID del rol
   * @returns {Promise<Object>} Resultado de la operación
   */
  async deactivateRole(roleId) {
    return await apiRequest(`/roles/desactivar/${roleId}`, {
      method: 'PUT',
    });
  },

  // ==================== OPERACIONES DE PERMISOS ====================

  /**
   * Obtiene todos los permisos
   * @returns {Promise<Array>} Lista de permisos
   */
  async getPermissions() {
    return await apiRequest('/permisos');
  },

  /**
   * Obtiene solo los permisos activos
   * @returns {Promise<Array>} Lista de permisos activos
   */
  async getActivePermissions() {
    return await apiRequest('/permisos/activos');
  },

  /**
   * Obtiene un permiso específico por ID
   * @param {string} permissionId - ID del permiso (UUID)
   * @returns {Promise<Object>} Datos del permiso
   */
  async getPermissionById(permissionId) {
    return await apiRequest(`/permisos/${permissionId}`);
  },

  /**
   * Crea un nuevo permiso
   * @param {Object} permissionData - Datos del permiso
   * @returns {Promise<Object>} Permiso creado
   */
  async createPermission(permissionData) {
    return await apiRequest('/permisos', {
      method: 'POST',
      body: JSON.stringify(permissionData),
    });
  },

  /**
   * Actualiza un permiso existente
   * @param {string} permissionId - ID del permiso
   * @param {Object} permissionData - Nuevos datos del permiso
   * @returns {Promise<Object>} Permiso actualizado
   */
  async updatePermission(permissionId, permissionData) {
    return await apiRequest(`/permisos/${permissionId}`, {
      method: 'PUT',
      body: JSON.stringify(permissionData),
    });
  },

  /**
   * Elimina un permiso
   * @param {string} permissionId - ID del permiso
   * @returns {Promise<Object>} Resultado de la operación
   */
  async deletePermission(permissionId) {
    return await apiRequest(`/permisos/${permissionId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Activa un permiso
   * @param {string} permissionId - ID del permiso
   * @returns {Promise<Object>} Resultado de la operación
   */
  async activatePermission(permissionId) {
    return await apiRequest(`/permisos/${permissionId}/activar`, {
      method: 'PUT',
    });
  },

  /**
   * Desactiva un permiso
   * @param {string} permissionId - ID del permiso
   * @returns {Promise<Object>} Resultado de la operación
   */
  async deactivatePermission(permissionId) {
    return await apiRequest(`/permisos/${permissionId}/desactivar`, {
      method: 'PUT',
    });
  },

  // ==================== OPERACIONES DE RELACIONES ROL-PERMISO ====================

  /**
   * Obtiene todas las relaciones rol-permiso
   * @returns {Promise<Array>} Lista de relaciones
   */
  async getRolePermissions() {
    return await apiRequest('/rol-permisos');
  },

  /**
   * Obtiene solo las relaciones activas
   * @returns {Promise<Array>} Lista de relaciones activas
   */
  async getActiveRolePermissions() {
    return await apiRequest('/rol-permisos/activos');
  },

  /**
   * Crea una nueva relación rol-permiso
   * @param {Object} relationData - Datos de la relación
   * @returns {Promise<Object>} Relación creada
   */
  async createRolePermission(relationData) {
    return await apiRequest('/rol-permisos', {
      method: 'POST',
      body: JSON.stringify(relationData),
    });
  },

  /**
   * Elimina una relación rol-permiso
   * @param {string} relationId - ID de la relación (UUID)
   * @returns {Promise<Object>} Resultado de la operación
   */
  async deleteRolePermission(relationId) {
    return await apiRequest(`/rol-permisos/${relationId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Activa una relación rol-permiso
   * @param {string} relationId - ID de la relación
   * @returns {Promise<Object>} Resultado de la operación
   */
  async activateRolePermission(relationId) {
    return await apiRequest(`/rol-permisos/${relationId}/activar`, {
      method: 'PUT',
    });
  },

  /**
   * Desactiva una relación rol-permiso
   * @param {string} relationId - ID de la relación
   * @returns {Promise<Object>} Resultado de la operación
   */
  async deactivateRolePermission(relationId) {
    return await apiRequest(`/rol-permisos/${relationId}/desactivar`, {
      method: 'PUT',
    });
  },

  // ==================== OPERACIONES COMBINADAS ====================

  /**
   * Obtiene los permisos de un rol específico
   * Esta función combina roles y relaciones para obtener los permisos activos de un rol
   * @param {number} roleId - ID del rol
   * @returns {Promise<Array>} Lista de permisos del rol
   */
  async getPermissionsByRole(roleId) {
    try {
      // Obtener todas las relaciones activas
      const relations = await this.getActiveRolePermissions();
      
      // Filtrar por rol específico
      const roleRelations = relations.filter(relation => 
        relation.rol_id === roleId || relation.id_rol === roleId
      );
      
      // Obtener IDs de permisos
      const permissionIds = roleRelations.map(relation => 
        relation.permiso_id || relation.id_permiso
      );
      
      // Obtener detalles de permisos
      const permissions = await this.getActivePermissions();
      
      // Filtrar permisos que están asignados al rol
      return permissions.filter(permission => 
        permissionIds.includes(permission.id_permiso || permission.permiso_id)
      );
      
    } catch (error) {
      console.error(`Error obteniendo permisos del rol ${roleId}:`, error);
      return [];
    }
  },

  /**
   * Actualiza todos los permisos de un rol
   * Esta función maneja la lógica compleja de agregar/quitar permisos de un rol
   * @param {number} roleId - ID del rol
   * @param {Array} newPermissionIds - Nuevos IDs de permisos para el rol
   * @returns {Promise<Object>} Resultado de la operación
   */
  async updateRolePermissions(roleId, newPermissionIds) {
    try {
      console.log(`Actualizando permisos del rol ${roleId}:`, newPermissionIds);
      
      // Obtener relaciones actuales del rol
      const currentRelations = await this.getActiveRolePermissions();
      const roleCurrentRelations = currentRelations.filter(relation => 
        (relation.rol_id === roleId || relation.id_rol === roleId)
      );
      
      const currentPermissionIds = roleCurrentRelations.map(relation => 
        relation.permiso_id || relation.id_permiso
      );
      
      // Identificar permisos a agregar y quitar
      const toAdd = newPermissionIds.filter(id => !currentPermissionIds.includes(id));
      const toRemove = currentPermissionIds.filter(id => !newPermissionIds.includes(id));
      
      const results = [];
      
      // Agregar nuevos permisos
      for (const permissionId of toAdd) {
        try {
          const result = await this.createRolePermission({
            rol_id: roleId,
            permiso_id: permissionId,
            estado: 'activo'
          });
          results.push({ action: 'added', permissionId, result });
        } catch (error) {
          console.error(`Error agregando permiso ${permissionId} al rol ${roleId}:`, error);
          results.push({ action: 'add_error', permissionId, error: error.message });
        }
      }
      
      // Quitar permisos eliminados
      for (const permissionId of toRemove) {
        try {
          // Encontrar la relación específica
          const relationToRemove = roleCurrentRelations.find(relation => 
            (relation.permiso_id === permissionId || relation.id_permiso === permissionId)
          );
          
          if (relationToRemove) {
            const relationId = relationToRemove.id_rol_permiso || relationToRemove.id;
            const result = await this.deleteRolePermission(relationId);
            results.push({ action: 'removed', permissionId, result });
          }
        } catch (error) {
          console.error(`Error quitando permiso ${permissionId} del rol ${roleId}:`, error);
          results.push({ action: 'remove_error', permissionId, error: error.message });
        }
      }
      
      console.log('Resultado de actualización de permisos:', results);
      
      return {
        success: true,
        message: `Permisos actualizados: ${toAdd.length} agregados, ${toRemove.length} quitados`,
        details: results
      };
      
    } catch (error) {
      console.error(`Error actualizando permisos del rol ${roleId}:`, error);
      throw error;
    }
  },

  /**
   * Obtiene una matriz completa de roles y permisos para la interfaz
   * USA ENDPOINTS REALES DEL BACKEND: /api/admin/roles-permissions/*
   * @returns {Promise<Object>} Objeto con roles, permisos y sus relaciones
   */
  async getRolePermissionMatrix() {
    try {
      console.log('🔄 Obteniendo matriz de roles y permisos del backend...');
      
      // Usar el endpoint admin que existe y funciona
      const response = await this.getAdminMatrix();
      
      // Verificar si la respuesta tiene la estructura esperada
      if (!response || !response.success || !response.data) {
        throw new Error('Respuesta inválida del servidor');
      }
      
      const adminMatrix = response.data;
      
      console.log('📊 Datos recibidos del admin endpoint:', adminMatrix);
      
      // Transformar los datos del formato admin al formato esperado por el frontend
      const roles = [];
      const permissions = new Map(); // Usar Map para evitar duplicados
      const relations = {};
      
      adminMatrix.forEach(rolData => {
        // Agregar rol
        roles.push({
          id: rolData.rol_id,
          rol_id: rolData.rol_id,
          name: rolData.nombre_rol,
          nombre: rolData.nombre_rol,
          estado: rolData.estado_rol
        });
        
        // Procesar permisos y relaciones
        const rolePermissions = [];
        rolData.permisos.forEach(permiso => {
          // Agregar permiso al mapa (evita duplicados)
          permissions.set(permiso.id_permiso, {
            id: permiso.id_permiso,
            id_permiso: permiso.id_permiso,
            name: permiso.nombre_permiso,
            nombre: permiso.nombre_permiso,
            description: permiso.ruta || permiso.descripcion || 'Sin descripción',
            descripcion: permiso.ruta || permiso.descripcion || 'Sin descripción'
          });
          
          // Si el permiso está activo, agregarlo a las relaciones
          if (permiso.activo) {
            rolePermissions.push(permiso.id_permiso);
          }
        });
        
        relations[rolData.rol_id] = rolePermissions;
      });
      
      // Convertir Map de permisos a array
      const permissionsArray = Array.from(permissions.values());
      
      console.log('✅ Matriz admin obtenida exitosamente');
      console.log('Roles:', roles.length);
      console.log('Permisos:', permissionsArray.length);
      console.log('Relaciones:', Object.keys(relations).length);
      
      return {
        roles: roles || [],
        permissions: permissionsArray || [],
        relations: relations,
        rawRelations: [], // No necesario con el formato admin
        adminData: adminMatrix // Guardar datos originales por si acaso
      };
      
    } catch (error) {
      console.error('❌ Error obteniendo matriz admin:', error);
      
      // NO usar fallback - solo reportar el error
      throw new Error(`Error cargando datos: ${error.message}`);
    }
  },

  /**
   * Actualiza todos los permisos de un rol
   * USA ENDPOINTS REALES DEL BACKEND: /api/admin/roles-permissions/*
   * @param {number} roleId - ID del rol
   * @param {Array} newPermissionIds - Nuevos IDs de permisos para el rol
   * @returns {Promise<Object>} Resultado de la operación
   */
  async updateRolePermissions(roleId, newPermissionIds) {
    try {
      console.log(`Actualizando permisos del rol ${roleId}:`, newPermissionIds);
      
      // Obtener relaciones actuales del rol usando el método admin
      const currentMatrix = await this.getAdminMatrix();
      
      if (!currentMatrix || !currentMatrix.success || !currentMatrix.data) {
        throw new Error('No se pudo obtener la matriz actual de permisos');
      }
      
      const currentRole = currentMatrix.data.find(r => r.rol_id === roleId);
      
      if (!currentRole) {
        throw new Error(`Rol ${roleId} no encontrado`);
      }
      
      // Obtener permisos actuales activos
      const currentPermissionIds = currentRole.permisos
        .filter(p => p.activo)
        .map(p => p.id_permiso);
      
      // Calcular cambios
      const toAdd = newPermissionIds.filter(id => !currentPermissionIds.includes(id));
      const toRemove = currentPermissionIds.filter(id => !newPermissionIds.includes(id));
      
      console.log(`Cambios calculados - Agregar: ${toAdd.length}, Quitar: ${toRemove.length}`);
      
      // Preparar actualizaciones para el endpoint masivo
      const updates = [];
      
      // Agregar nuevos permisos
      toAdd.forEach(permissionId => {
        updates.push({
          rol_id: roleId,
          id_permiso: permissionId,
          estado: true
        });
      });
      
      // Quitar permisos
      toRemove.forEach(permissionId => {
        updates.push({
          rol_id: roleId,
          id_permiso: permissionId,
          estado: false
        });
      });
      
      // Si hay cambios, ejecutar actualización masiva
      if (updates.length > 0) {
        const resultado = await this.updatePermissionsBulkAdmin(updates);
        
        console.log('Resultado de actualización masiva:', resultado);
        
        return {
          success: true,
          message: `Permisos actualizados: ${toAdd.length} agregados, ${toRemove.length} quitados`,
          details: resultado
        };
      } else {
        return {
          success: true,
          message: 'No hay cambios que aplicar',
          details: { updates: [] }
        };
      }
      
    } catch (error) {
      console.error(`Error actualizando permisos del rol ${roleId}:`, error);
      throw error;
    }
  }
};

export default rolePermissionService;